'use strict';

function formatPriceToEnd999(price) {
  if (!price || price <= 0) return 0;

  const priceStr = Math.floor(price).toString();
  if (priceStr.endsWith('999')) return price;

  return (Math.floor(price / 1000) * 1000) + 999;
}

function normalizeManufacturingTimeDays(value) {
  if (value === '' || value === null || value === undefined) return null;
  const normalized = String(value).trim();
  if (!/^(?:[1-9]|[1-5]\d|60)$/.test(normalized)) return null;
  return Number(normalized);
}

function applyBulkEditChanges(products, selectedProductIds, changes, { isAmazonFile = false, includeCategory = true } = {}) {
  const selectedIds = new Set(Array.isArray(selectedProductIds) ? selectedProductIds : []);

  return (Array.isArray(products) ? products : []).map(product => {
    if (!selectedIds.has(product?.id)) return product;

    const updated = { ...product };

    if (includeCategory && changes?.category) {
      updated.category = changes.category;
      updated.categoria = changes.category;
      updated.category_id = changes.category;
    }

    const priceAdjustment = changes?.priceAdjustment;

    // ÚNICA configuración que determina si el precio final debe terminar en 999.
    // Se conserva en el producto para que el motor de publicación respete
    // exactamente lo seleccionado en Edición Masiva.
    updated.forceEnd999 = priceAdjustment?.forceEnd999 === true;
    if (
      priceAdjustment?.forceEnd999 === true ||
      priceAdjustment?.value !== 0 ||
      priceAdjustment?.type === 'plus_40_percent'
    ) {
      const basePrice = updated?.bulkEditBasePrice ?? updated?.price;
      let newPrice = Number(basePrice);
      if (!Number.isFinite(newPrice)) newPrice = 0;

      if (priceAdjustment?.type === 'percentage') {
        newPrice = basePrice * (1 + priceAdjustment?.value / 100);
      } else if (priceAdjustment?.type === 'plus_40_percent') {
        newPrice = basePrice * 1.4;
      } else if (priceAdjustment?.type === 'fixed') {
        newPrice = basePrice + priceAdjustment?.value;
      }

      if (priceAdjustment?.forceEnd999) {
        newPrice = formatPriceToEnd999(newPrice);
      }

      updated.price = Math.round(newPrice);
      updated.precio = updated.price;
      updated.bulkEditBasePrice = Number.isFinite(Number(basePrice)) ? Number(basePrice) : 0;
    }

    const productAsin = String(
      updated?.asin ||
      updated?.ASIN ||
      updated?.amazon_asin ||
      updated?.originalAsin ||
      ""
    ).trim().toUpperCase();

    const engineFinalPrice = Number(
      changes?.pricingFinalPricesByAsin?.[productAsin]
    );

    if (Number.isFinite(engineFinalPrice) && engineFinalPrice > 0) {
      updated.price = Math.round(engineFinalPrice);
      updated.precio = updated.price;
    }

    if (changes?.descriptionTemplate) {
      const description = changes.descriptionTemplate.replace('{title}', updated?.title);
      updated.description = description;
      updated.descripcion = description;
    }

    if (changes?.condition) {
      updated.condition = changes.condition;
      updated.condicion = changes.condition;
    }

    if (changes?.storeName !== undefined) {
      updated.storeName = changes.storeName;
      updated.store_name = changes.storeName;
    }

    if (changes?.shipping && typeof changes.shipping.free === 'boolean') {
      updated.envioGratis = changes.shipping.free ? 'Sí' : 'No';
      updated.free_shipping = changes.shipping.free;
    }

    const manufacturingTimeDays = normalizeManufacturingTimeDays(changes?.manufacturingTimeDays);
    if (manufacturingTimeDays !== null) {
      updated.manufacturingTimeDays = manufacturingTimeDays;
    }

    if (isAmazonFile) {
      updated.availabilityDays = 12;
      updated.availableUntil = new Date(Date.now() + (12 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
    }

    return updated;
  });
}

function createQueueBulkEditPreset(changes) {
  return {
    priceAdjustment: { ...(changes?.priceAdjustment || {}) },
    pricingFinalPricesByAsin: { ...(changes?.pricingFinalPricesByAsin || {}) },
    condition: changes?.condition || '',
    shipping: { ...(changes?.shipping || {}) },
    storeName: changes?.storeName || '',
    ...(normalizeManufacturingTimeDays(changes?.manufacturingTimeDays) !== null
      ? { manufacturingTimeDays: normalizeManufacturingTimeDays(changes.manufacturingTimeDays) }
      : {}),
    ...(changes?.descriptionTemplate ? { descriptionTemplate: changes.descriptionTemplate } : {})
  };
}

function resolveBatchCategory(batch, preset) {
  return batch?.categoryOverride
    || batch?.manualCategoryId
    || batch?.detectedCategoryId
    || batch?.detectedCategory
    || (Array.isArray(batch?.products)
      ? batch.products.find(product => product?.category_id || product?.categoria || product?.category)?.category_id
        || batch.products.find(product => product?.categoria || product?.category)?.categoria
        || batch.products.find(product => product?.category)?.category
      : '')
    || preset?.category
    || '';
}

function applyQueueBulkEditPresetToBatch(batch, preset, { applyAllProducts = false } = {}) {
  if (!batch || !preset || batch.bulkPresetApplied) return batch;

  const products = Array.isArray(batch.products) ? batch.products : [];
  const selectedProductIds = !applyAllProducts && Array.isArray(batch.selectedProducts) && batch.selectedProducts.length > 0
    ? batch.selectedProducts
    : products.map(product => product?.id).filter(Boolean);
  const category = resolveBatchCategory(batch, preset);
  const changes = { ...preset, category };

  return {
    ...batch,
    products: applyBulkEditChanges(products, selectedProductIds, changes, {
      isAmazonFile: Boolean(batch.isAmazonFile),
      includeCategory: Boolean(category)
    }),
    appliedCategoryId: category || null,
    appliedCategoryPath: changes?.categoryPath || null,
    categoryPending: !category,
    bulkPresetApplied: true
  };
}

module.exports = {
  applyBulkEditChanges,
  applyQueueBulkEditPresetToBatch,
  createQueueBulkEditPreset,
  formatPriceToEnd999,
  normalizeManufacturingTimeDays,
  resolveBatchCategory
};